import { Menu, Transition, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import Typography from '~/shared/components/Typography';
import Tag, { type TagVariant } from '~/shared/components/Tag';

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

export default function AutoscaleRulesDropdown<T extends { title: string }>({
  items,
  onSelect,
  selectedItem,
  disabledTitles = [],
  selectedAsTag = false,
  onClearSelected,
  disabled = false,
}: {
  onSelect: (selection: T) => void;
  items?: T[];
  selectedItem?: T;
  disabledTitles?: string[];
  selectedAsTag?: boolean;
  onClearSelected?: () => void;
  disabled?: boolean;
}) {
  const disabledSet = new Set(disabledTitles);
  const isTagItem = (val: unknown): val is { title: string; tag: TagVariant } => {
    return (
      typeof val === 'object' &&
      val !== null &&
      'tag' in val &&
      typeof (val as { tag: unknown }).tag === 'string'
    );
  };

  return (
    <div className="relative z-[9999] w-[160px]">
      <Menu as="div" className="relative flex w-full">
        {({ open }) => (
          <>
            <MenuButton
              disabled={disabled}
              className={clsx(
                'text-primary-900 border border-gray-300 flex justify-between rounded-[8px] p-3 h-11 2xl:h-13',
                'w-[160px]',
                disabled
                  ? 'bg-white text-gray-400 border-gray-300 cursor-not-allowed pointer-events-none'
                  : open
                    ? 'border-primary-300 cursor-pointer'
                    : 'hover:border-primary-300 active:border-primary-100 cursor-pointer'
              )}
            >
              <div className="flex items-center justify-between w-full gap-2">
                <div className={clsx('flex items-center gap-1.5 overflow-hidden flex-1')}>
                  {selectedAsTag && selectedItem ? (
                    <Tag
                      variant={isTagItem(selectedItem) ? selectedItem.tag : 'gray'}
                      size="sm"
                      text={selectedItem.title}
                      onRemove={onClearSelected}
                    />
                  ) : isTagItem(selectedItem) ? (
                    <Tag variant={selectedItem.tag} size="sm" text={selectedItem.title} />
                  ) : (
                    <Typography
                      color={disabled && !selectedItem ? 'text-gray-400' : 'text-primary-900'}
                      variant="p3-reg"
                    >
                      {selectedItem?.title || 'Select'}
                    </Typography>
                  )}
                </div>
                <ChevronDownIcon
                  className={clsx(
                    'w-[14px] h-[14px] stroke-3',
                    disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-primary-900 cursor-pointer',
                    !disabled && open && 'rotate-180'
                  )}
                />
              </div>
            </MenuButton>

            <Transition as={Fragment} {...transitionProps}>
              <Portal>
                <MenuItems
                  anchor={'bottom start'}
                  className={clsx(
                    'rounded-lg mt-2.5 bg-white shadow-filter-dropdown border border-primary-70 focus:outline-hidden z-[99999]'
                  )}
                >
                  <div className={clsx('flex flex-col', 'w-[160px]')}>
                    {items?.map((item, index) => {
                      const isDisabled = disabledSet.has(item.title);
                      return (
                        <MenuItem
                          as={'button'}
                          key={`${item.title}-${index}`}
                          disabled={isDisabled}
                          className={clsx(
                            'text-sm leading-6 w-full h-12 2xl:h-14 flex items-center justify-between px-5 py-[18px] border-b border-gray-200 first:rounded-t-lg group',
                            selectedItem?.title === item.title && 'bg-primary-50',
                            isDisabled
                              ? 'opacity-50  cursor-not-allowed'
                              : 'hover:bg-primary-20 cursor-pointer'
                          )}
                          onClick={() => {
                            if (!isDisabled) {
                              onSelect(item as T);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {isTagItem(item) ? (
                              <Tag variant={item.tag} size="sm" text={item.title} />
                            ) : (
                              <Typography color="text-primary-900" variant="p3-reg">
                                {item.title}
                              </Typography>
                            )}
                          </div>
                        </MenuItem>
                      );
                    })}
                  </div>
                </MenuItems>
              </Portal>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}
